/**
 * ENG-6105: accept the cached invitation once a Cognito session exists.
 *
 * The legacy login hands the cached invitation to createToken, which accepts it
 * as a side effect. A Cognito sign-in has no such hook, so every path that ends
 * a Cognito login (the password form, and MFA enrolment when the api's gate
 * held the session) calls this before its redirect. Invitation tokens stay the
 * api's HS256 JWTs, unrelated to the session.
 */
import * as Sentry from '@sentry/react';

import { notify } from '@/actions/ui';
import { ACCEPT_PROJECT_INVITATION_MUTATION } from '@/auth/store/queries';
import config from '@/config';
import { GraphQLClient, firstErrorCode } from '@/core/api';
import {
  INVITATION_NOT_ACCEPTED_NOTICE,
  getInvitationToken,
  isTerminalInvitationError,
  settleCachedInvitation,
} from '@/core/invite';
import { savePostLoginNotice } from '@/core/postLoginNotice';
import store from '@/store';
import { rewriteUrl } from '@/utils';

interface Outcome {
  token: string;
  /** The api's destination for the invitation, when it gave one. */
  destination?: string;
  /** Accepted, or refused for good: nothing left to retry. */
  final: boolean;
}

// The user is already signed in, so a failure never fails the login: it is
// reported and the user is told after the reload. The cache is settled only
// for this token, since another tab may have cached a different one meanwhile.
async function acceptCachedInvitation(next: string): Promise<Outcome | undefined> {
  const token = getInvitationToken();
  if (!token || nextAcceptsInvitation(next, token)) return undefined;
  try {
    // No explicit auth: authLink sends the active Cognito ID token.
    const data = await new GraphQLClient(config.apiUrl).mutate(ACCEPT_PROJECT_INVITATION_MUTATION, {
      token,
    });
    settleCachedInvitation(token);
    const destination = data.acceptInvitation ? rewriteUrl(data.acceptInvitation) : undefined;
    return { token, destination, final: true };
  } catch (err) {
    notifyInvitationNotAccepted(err);
    savePostLoginNotice(INVITATION_NOT_ACCEPTED_NOTICE, 'error');
    settleCachedInvitation(token, err);
    return { token, final: isTerminalInvitationError(err) };
  }
}

// Split at the first `#` and the first `?` only: both may appear again inside
// a query value, such as a nested `next=/browse?query=people`.
function splitUrl(url: string): { path: string; query: string; hash?: string } {
  const hashAt = url.indexOf('#');
  const beforeHash = hashAt === -1 ? url : url.slice(0, hashAt);
  const hash = hashAt === -1 ? undefined : url.slice(hashAt + 1);
  const queryAt = beforeHash.indexOf('?');
  if (queryAt === -1) return { path: beforeHash, query: '', hash };
  return { path: beforeHash.slice(0, queryAt), query: beforeHash.slice(queryAt + 1), hash };
}

// `next` minus a settled `?invite=`: the reload would otherwise cache it again
// (saveInvitationToken) and the next login would resubmit it.
function withoutInvite(next: string, token: string): string {
  const { path, query, hash } = splitUrl(next);
  if (!query) return next;
  const params = new URLSearchParams(query);
  if (params.get('invite') !== token) return next;
  params.delete('invite');
  const rest = params.toString();
  return `${path}${rest ? `?${rest}` : ''}${hash !== undefined ? `#${hash}` : ''}`;
}

// RegisterPage (`/:subdomain?/signup/:type`) accepts `?invite=` for a signed-in
// viewer whenever a signup type is present; without one it redirects first.
// The whole pathname must match: `/signup/expert/basic-information` is the
// application route, which does not accept invitations.
const REGISTER_WITH_TYPE = /^(?:\/[^/]+)?\/signup\/[^/]+\/?$/;

// A `next` that lands on RegisterPage with the same invitation gets it accepted
// there; accepting here too would run the mutation twice. Any other `next`
// (the dashboard, `/signup` with no type) has no handler, so accept here.
function nextAcceptsInvitation(next: string, token: string): boolean {
  const { path, query } = splitUrl(next);
  return REGISTER_WITH_TYPE.test(path) && new URLSearchParams(query).get('invite') === token;
}

// The APIError carries the mutation's variables, and those hold the invitation
// JWT. Report a fresh error with only the code and message.
function reportInvitationFailure(err: unknown): void {
  const code = firstErrorCode(err);
  const scrubbed = new Error(err instanceof Error ? err.message : String(err));
  scrubbed.name = 'InvitationAcceptError';
  Sentry.captureException(scrubbed, {
    level: 'warning',
    tags: { login_step: 'accept_invitation', error_code: code ?? 'none' },
  });
}

/** Report a failed acceptInvitation (scrubbed) and tell the user. */
export function notifyInvitationNotAccepted(err: unknown): void {
  reportInvitationFailure(err);
  store.dispatch(notify(INVITATION_NOT_ACCEPTED_NOTICE, 'error'));
}

/**
 * Accept the cached invitation, if any, and resolve with where to go next: the
 * api's destination for the invitation, else `next`. Never rejects: the user is
 * already signed in, so a failure is reported, the user is told after the
 * reload, and the login carries on.
 */
export async function acceptInvitationAfterLogin(next: string): Promise<string> {
  const outcome = await acceptCachedInvitation(next);
  if (!outcome) return next;
  if (outcome.destination) return outcome.destination;
  return outcome.final ? withoutInvite(next, outcome.token) : next;
}
